import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtQty, fmtDate, fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const STATUSES = ["available", "quarantine", "hold", "damaged"];

export default function Quality() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [holds, setHolds] = useState([]);
  const [moves, setMoves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [dlg, setDlg] = useState(null); // {item_number, warehouse, location, from_status, max}
  const [form, setForm] = useState({ qty: "", to_status: "", reason: "" });

  const load = useCallback(() => {
    api.get("/quality/holds").then((r) => setHolds(r.data));
    api.get("/quality/moves").then((r) => setMoves(r.data));
    api.get("/balances").then((r) => setBalances(r.data.filter((b) => b.stock_status === "available" && b.qty_on_hand > 0)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMove = (b) => {
    setDlg(b);
    setForm({ qty: b.qty_on_hand, to_status: "", reason: "" });
  };

  const post = async () => {
    try {
      await api.post("/quality/move", {
        item_number: dlg.item_number, warehouse: dlg.warehouse, location: dlg.location || "",
        qty: Number(form.qty), from_status: dlg.stock_status, to_status: form.to_status, reason: form.reason,
      });
      toast.success(t("qa.moved"));
      setDlg(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const HoldTable = ({ rows, testid, actionLabel }) => (
    rows.length === 0 ? <Empty text={t("common.noData")} /> : (
      <div className="tbl-wrap mt-3" data-testid={testid}>
        <table className="tbl">
          <thead><tr>
            <th>{t("common.item")}</th><th>{t("common.description")}</th><th>{t("common.warehouse")}</th>
            <th>{t("loc.bin")}</th><th>{t("common.status")}</th>
            <th className="num">{t("cx.onHand")}</th><th className="num">{t("cx.value")}</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td className="font-bold">{b.item_number}</td>
                <td>{b.description}</td>
                <td>{b.warehouse}</td>
                <td>{b.location || "—"}</td>
                <td><StatusBadge status={b.stock_status} /></td>
                <td className="num font-bold">{fmtQty(b.qty_on_hand)}</td>
                <td className="num">{fmtMoney(b.value ?? b.qty_on_hand * (b.avg_cost || 0))}</td>
                <td>
                  {can("admin", "warehouse", "approver") && (
                    <button onClick={() => openMove(b)} data-testid={`quality-move-${b.item_number}-${b.stock_status}`}
                      className="border border-primary text-primary px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-primary hover:text-primary-foreground transition-colors">
                      {actionLabel}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  );

  return (
    <div data-testid="quality-page">
      <PageHeader title={t("qa.title")} subtitle={t("qa.subtitle")} />

      <Tabs defaultValue="holds">
        <TabsList className="rounded-none">
          <TabsTrigger value="holds" className="rounded-none text-xs" data-testid="tab-holds">{t("qa.holds")}</TabsTrigger>
          <TabsTrigger value="available" className="rounded-none text-xs" data-testid="tab-available">{t("st.available")}</TabsTrigger>
          <TabsTrigger value="moves" className="rounded-none text-xs" data-testid="tab-quality-moves">{t("qa.moves")}</TabsTrigger>
        </TabsList>
        <TabsContent value="holds">
          <HoldTable rows={holds} testid="holds-table" actionLabel={t("qa.release")} />
          <p className="text-[10px] text-muted-foreground mt-2">{t("qa.releaseHint")}</p>
        </TabsContent>
        <TabsContent value="available">
          <HoldTable rows={balances} testid="available-table" actionLabel={t("qa.newMove")} />
        </TabsContent>
        <TabsContent value="moves">
          {moves.length === 0 ? <Empty text={t("common.noData")} /> : (
            <div className="tbl-wrap mt-3" data-testid="quality-moves-table">
              <table className="tbl">
                <thead><tr>
                  <th>{t("common.number")}</th><th>{t("common.item")}</th><th className="num">{t("common.qty")}</th>
                  <th>{t("qa.fromStatus")}</th><th></th><th>{t("qa.toStatus")}</th>
                  <th>{t("cx.reason")}</th><th>{t("common.user")}</th><th>{t("common.date")}</th>
                </tr></thead>
                <tbody>
                  {moves.map((m) => (
                    <tr key={m.id}>
                      <td className="font-bold">{m.number}</td>
                      <td>{m.item_number}</td>
                      <td className="num font-bold">{fmtQty(m.qty)}</td>
                      <td><StatusBadge status={m.from_status} /></td>
                      <td><ArrowRight size={12} className="text-muted-foreground" /></td>
                      <td><StatusBadge status={m.to_status} /></td>
                      <td className="text-muted-foreground">{m.reason}</td>
                      <td className="text-muted-foreground">{m.created_by}</td>
                      <td className="text-muted-foreground">{fmtDate(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="rounded-none">
          {dlg && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black flex items-center gap-2">
                  {dlg.item_number} <StatusBadge status={dlg.stock_status} />
                </DialogTitle>
                <DialogDescription className="text-xs">{t("qa.releaseHint")}</DialogDescription>
              </DialogHeader>
              <div className="text-xs text-muted-foreground">{dlg.description} · {dlg.warehouse}{dlg.location ? ` / ${dlg.location}` : ""} · {t("cx.onHand")}: {fmtQty(dlg.qty_on_hand)}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.qty")}</Label>
                  <Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="quality-qty-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("qa.toStatus")}</Label>
                  <Select value={form.to_status} onValueChange={(v) => setForm({ ...form, to_status: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="quality-to-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.filter((s) => s !== dlg.stock_status).map((s) => <SelectItem key={s} value={s}>{t(`st.${s}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cx.reason")}</Label>
                  <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="quality-reason-input" />
                </div>
              </div>
              <Button onClick={post} disabled={!form.to_status || !form.qty}
                className="rounded-none font-bold" data-testid="quality-post-button">{t("qa.newMove")}</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
